// ============================================================================
// Ambulance role API helpers.
//
// Backend: clinical_notes.AmbulanceRequest. Status transitions flow
//   requested -> accepted -> dispatched -> completed   (happy path)
//   requested -> rejected                              (unable to serve)
//   requested -> cancelled                             (by requester)
//
// Endpoints:
//   GET   /api/clinical-notes/ambulance-requests/           list
//   GET   /api/clinical-notes/ambulance-requests/{id}/      detail
//   PATCH /api/clinical-notes/ambulance-requests/{id}/ { status, clinical_summary? }
// ============================================================================

import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

const detail = (id) => `${endpoints.ambulanceList}${id}/`

export async function getAmbulanceQueue() {
  const { data } = await api.get(endpoints.ambulanceList, {
    params: { ordering: '-created_at' },
  })
  return unwrapList(data)
}

export async function getAmbulanceRequest(id) {
  const { data } = await api.get(detail(id))
  return data
}

export async function updateAmbulanceStatus(id, status, summary) {
  const body = { status }
  if (summary) body.clinical_summary = summary
  const { data } = await api.patch(detail(id), body)
  return data
}

/**
 * Patient-initiated transport request from the /patient/ambulance
 * marketplace. The backend serializer auto-fills `patient` from
 * request.user.patient_profile when neither encounter nor patient is
 * supplied, so the FE just sends the dispatch fields. Doctor-initiated
 * requests still go through the CNE as before.
 *
 * Required fields: service_type, pickup_location, urgency. Optional:
 * destination, clinical_summary, ambulance_service.
 */
export async function createAmbulanceRequest(payload) {
  const { data } = await api.post(endpoints.ambulanceCreate, payload)
  return data
}

// ---------------------------------------------------------------------------
// Service profile (branding) — separate from the dispatch queue. Surfaced
// on the AmbulanceDashboard so dispatchers can upload their service logo
// once and have it appear on the patient marketplace + dispatch surfaces.
// ---------------------------------------------------------------------------

const profileDetail = (id) => `${endpoints.ambulanceServiceList}${id}/`

export async function getAmbulanceServiceProfile(id) {
  const { data } = await api.get(profileDetail(id))
  return data
}

/**
 * Upload (or replace) an ambulance service's logo. Ambulance staff can
 * write to their own service profile via `IsAmbulanceStaff` on the
 * AmbulanceServiceProfileViewSet. Pass `null` to clear.
 */
export async function setAmbulanceServiceLogo(id, file) {
  const fd = new FormData()
  if (file) {
    fd.append('logo', file)
  } else {
    fd.append('logo', '')
  }
  const { data } = await api.patch(profileDetail(id), fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

// ---------------------------------------------------------------------------
// Ambulance package catalogue (AmbulanceServiceItem). Ambulance staff manage
// their own service's packages; doctors read them to pick at dispatch.
// ---------------------------------------------------------------------------
const itemDetail = (id) => `${endpoints.ambulanceItems}${id}/`

export async function listAmbulanceItems(params = {}) {
  // Default to the whole catalogue in one request (no page controls; rendered
  // client-side). 1000 = paginator max_page_size; without page_size the list is
  // silently capped at the 50-row default. Callers may override page_size.
  const { data } = await api.get(endpoints.ambulanceItems, {
    params: { page_size: 1000, ...params },
  })
  return unwrapList(data)
}
export async function createAmbulanceItem(payload) {
  const { data } = await api.post(endpoints.ambulanceItems, payload)
  return data
}
export async function updateAmbulanceItem(id, patch) {
  const { data } = await api.patch(itemDetail(id), patch)
  return data
}
export async function deleteAmbulanceItem(id) {
  await api.delete(itemDetail(id))
}
